import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ToCustomDecimal } from "../../AppSystem/Helpers/ToCustomDecimal";
import { SlidePanel } from "../../Common/Components/Modal/SlidePanel";
import { ChangeDateFormat } from "../../Common/Helpers/ChangeDateFormat";

const ReimbursementHeader = ({
    createButton,             // JSX element — rendered as-is, no hardcoded button here
    title,
    selectedMonth,
    onPrevMonth,
    onNextMonth,
    cards,
    loading = false,
    renderItemActions,
    showSummary = true,       // month selector + summary boxes
}) => {
    const { t } = useTranslation();
    const [panelOpen, setPanelOpen] = useState(false);
    const [panelKey, setPanelKey] = useState(null);

    const [year, monthNum] = selectedMonth.split("-").map(Number);

    const monthLabel = (() => {
        const label = new Date(year, monthNum - 1, 1).toLocaleDateString("sv-SE", {
            month: "long", year: "numeric",
        });
        return label.charAt(0).toUpperCase() + label.slice(1);
    })();

    const openPanel = (key) => { setPanelKey(key); setPanelOpen(true); };
    const closePanel = () => setPanelOpen(false);

    const activeCard = cards?.find(c => c.key === panelKey);
    const panelItems = [...(activeCard?.items ?? [])].sort((a, b) => {
        const dateA = new Date(a.paidDateTime || a.date || a.createdAt || 0).getTime();
        const dateB = new Date(b.paidDateTime || b.date || b.createdAt || 0).getTime();
        return dateB - dateA;
    });

    const getThemeKey = (key) => {
        if (["pending", "denied", "draft"].includes(key)) return key;
        return "default";
    };

    const getThemeClass = (key) => `rh-theme-${getThemeKey(key)}`;

    const getThemeIcon = (key) => {
        switch (getThemeKey(key)) {
            case "pending":
                return <i className="bx bxs-hourglass-top rh-theme-icon rh-theme-icon-pending" />;
            case "denied":
                return <i className="bx bx-x-circle rh-theme-icon rh-theme-icon-denied" />;
            case "draft":
                return <i className="bx bxs-pencil rh-theme-icon rh-theme-icon-draft" />;
            default:
                return <span className="rh-theme-icon rh-theme-icon-default">•</span>;
        }
    };

    const formatDate = (value) => {
        if (!value) return "";
        return ChangeDateFormat('date', value);
    };

    const getMobileRowStatusMeta = (status) => {
        switch (status) {
            case "Godkänd":
                return { label: "Godkänd för utbetalning", statusClass: "reimbursement-card-status-approved" };
            case "Skickat":
                return { label: "Inväntar godkännande", statusClass: "reimbursement-card-status-sent" };
            case "Nekad":
                return { label: "Nekade ärenden", statusClass: "reimbursement-card-status-denied" };
            case "Förslag":
                return { label: "Utkast", statusClass: "reimbursement-card-status-draft" };
            default:
                return { label: status || "", statusClass: "reimbursement-card-status-default" };
        }
    };

    const getAmountColorClass = (item) => (item?.grossAmount ?? 0) < 0
        ? "reimbursement-card-amount-negative"
        : "reimbursement-card-amount-positive";

    const formatCardAmount = (value) => {
        if (typeof value === "string") return value;
        return `${ToCustomDecimal(value ?? 0)} kr`;
    };

    const getDeniedReasonText = (item) => {
        if (item?.status !== "Nekad") return "";
        const reason = item?.Reason ?? item?.reason ?? "";
        return typeof reason === "string" ? reason.trim() : "";
    };

    const inferActionLabelFromIconClass = (iconClass = "") => {
        const cls = iconClass.toLowerCase();
        if (cls.includes("eye") || cls.includes("show") || cls.includes("search")) return "Visa";
        if (cls.includes("edit") || cls.includes("pen") || cls.includes("pencil")) return "Redigera";
        if (cls.includes("trash") || cls.includes("delete") || cls.includes("times") || cls.includes("x-circle")) return "Ta bort";
        if (cls.includes("download") || cls.includes("file")) return "Ladda ner";
        return "";
    };

    const normalizeText = (value) => (typeof value === "string" ? value.replace(/\s+/g, " ").trim() : "");

    const readTextContent = (node) => {
        if (typeof node === "string") return normalizeText(node);
        if (!React.isValidElement(node)) return "";

        const children = React.Children.toArray(node.props?.children);
        const textParts = children
            .map((child) => readTextContent(child))
            .filter((text) => !!text);

        return normalizeText(textParts.join(" "));
    };

    const isTooltipNode = (node) => {
        if (!React.isValidElement(node)) return false;
        const className = (node.props?.className || "").toString().toLowerCase();
        return className.includes("tooltip");
    };

    const findTooltipText = (node) => {
        if (!React.isValidElement(node)) return "";

        if (isTooltipNode(node)) {
            const tooltipText = readTextContent(node);
            if (tooltipText) return tooltipText;
        }

        const children = React.Children.toArray(node.props?.children);
        for (const child of children) {
            const nestedTooltipText = findTooltipText(child);
            if (nestedTooltipText) return nestedTooltipText;
        }

        return "";
    };

    const extractActionLabel = (node) => {
        if (!React.isValidElement(node)) return "";

        const tooltipText = findTooltipText(node);
        if (tooltipText) return tooltipText;

        const direct = node.props?.["aria-label"] || node.props?.title;
        if (typeof direct === "string" && direct.trim()) return direct.trim();

        const children = React.Children.toArray(node.props?.children);
        for (const child of children) {
            if (typeof child === "string" && child.trim()) return child.trim();
            if (React.isValidElement(child)) {
                const nestedLabel = extractActionLabel(child);
                if (nestedLabel) return nestedLabel;

                const childClass = child.props?.className || "";
                const labelFromClass = inferActionLabelFromIconClass(childClass);
                if (labelFromClass) return labelFromClass;
            }
        }

        return "";
    };

    const stripTooltipNodes = (nodes) => {
        return React.Children.map(nodes, (node) => {
            if (!React.isValidElement(node)) return node;
            if (isTooltipNode(node)) return null;

            const cleanedChildren = node.props?.children
                ? stripTooltipNodes(node.props.children)
                : node.props?.children;

            return React.cloneElement(node, {
                ...node.props,
                children: cleanedChildren,
            });
        });
    };

    const enhanceActionMenuNodes = (nodes) => {
        return React.Children.map(nodes, (node) => {
            if (!React.isValidElement(node)) return node;

            const enhancedChildren = node.props?.children
                ? enhanceActionMenuNodes(node.props.children)
                : node.props?.children;

            const elementType = typeof node.type === "string" ? node.type.toLowerCase() : "";
            const tooltipText = findTooltipText(node);
            const isClickableEntry =
                elementType === "button" ||
                elementType === "a" ||
                (!!node.props?.onClick && !!tooltipText) ||
                (!!node.props?.href && !!tooltipText);

            if (!isClickableEntry) {
                return React.cloneElement(node, {
                    ...node.props,
                    children: enhancedChildren,
                });
            }

            const existingClassName = node.props?.className || "";
            const label = normalizeText(tooltipText || extractActionLabel(node));
            const iconOnlyChildren = stripTooltipNodes(enhancedChildren);

            return React.cloneElement(node, {
                ...node.props,
                className: `${existingClassName} reimbursement-action-menu-entry`.trim(),
                children: (
                    <>
                        <span className="reimbursement-action-menu-entry-icon">{iconOnlyChildren}</span>
                        {!!label && <span className="reimbursement-action-menu-entry-label">{label}</span>}
                    </>
                ),
            });
        });
    };

    const ActionMenu = ({ item }) => {
        const [isOpen, setIsOpen] = useState(false);
        const menuRef = useRef(null);
        const rawActionContent = renderItemActions ? renderItemActions(item) : null;
        const actionContent = enhanceActionMenuNodes(rawActionContent);

        useEffect(() => {
            const handleClickOutside = (event) => {
                if (menuRef.current && !menuRef.current.contains(event.target)) {
                    setIsOpen(false);
                }
            };
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }, []);

        if (!renderItemActions) {
            return <i className="bx bx-dots-vertical-rounded reimbursement-card-actions-disabled" />;
        }

        return (
            <div ref={menuRef} className="reimbursement-action-menu-wrap">
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(!isOpen);
                    }}
                    className="reimbursement-action-menu-trigger"
                >
                    <i className={`bx ${isOpen ? "bx-x" : "bx-dots-vertical-rounded"} reimbursement-action-menu-icon`} />
                </div>

                {isOpen && (
                    <div className="reimbursement-action-menu-dropdown">
                        <div className="action-menu-item">
                            {actionContent}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div className="rh-wrapper">
                <div className="rh-inner pt-0">
                    <div className="rh-header-row">
                        <h2 className="pb-2 ps-1 rokkitt-font mb-0">
                            {title ?? t('reimbursement.reportProcessings')}
                        </h2>
                        {createButton && <div>{createButton}</div>}
                    </div>

                    {showSummary && (
                        <>
                            <div className="rh-month-selector">
                                <button onClick={onPrevMonth} className="rh-nav-btn"><i className="bx bx-chevron-left fs-3" /></button>
                                <span className="rh-month-label">{monthLabel}</span>
                                <button onClick={onNextMonth} className="rh-nav-btn"><i className="bx bx-chevron-right fs-3" /></button>
                            </div>

                            <hr className="d-none d-lg-block my-3 rh-desktop-divider" />

                            <div className="rh-cards-grid">
                                {(cards ?? []).map((card) => (
                                    <div key={card.key} className={`rh-card ${getThemeClass(card.key)}`}>
                                        <div className={`rh-card-title ${getThemeClass(card.key)}-accent`}>{card.title}</div>
                                        <div className="rh-card-amount-row">
                                            <span className="rh-card-icon">{card.icon}</span>
                                            <span className={`rh-card-amount ${getThemeClass(card.key)}-accent`}>
                                                {loading ? "…" : formatCardAmount(card?.amount)}
                                            </span>
                                        </div>
                                        <div className="rh-card-footer">
                                            <span className={`rh-card-subtitle ${getThemeClass(card.key)}-accent`}>{card.subtitle}</span>
                                            <button type="button" onClick={() => openPanel(card.key)} className="rh-card-expand-btn" aria-label={card.title}>
                                                <i className="bx bx-chevron-down fs-2" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {showSummary && <hr className="d-none d-lg-block my-3 rh-desktop-divider" />}
                </div>
            </div>

            <SlidePanel isOpen={panelOpen} onClose={closePanel} title={activeCard?.title ?? ""}>
                <div className="rh-panel-wrapper">
                    <div className={`rh-panel-card ${getThemeClass(panelKey)}`}>
                        <div className={`rh-panel-card-title ${getThemeClass(panelKey)}-accent`}>{activeCard?.title}</div>
                        <div className="rh-panel-card-amount-row">
                            <span className="rh-panel-card-icon">{getThemeIcon(panelKey)}</span>
                            <span className={`rh-panel-card-amount ${getThemeClass(panelKey)}-accent`}>{formatCardAmount(activeCard?.amount)}</span>
                        </div>
                        <div className={`rh-panel-card-subtitle ${getThemeClass(panelKey)}-accent`}>{activeCard?.subtitle}</div>

                        <div className="rh-panel-items reimbursement-cards-list">
                            {panelItems.length === 0 ? (
                                <div className="rh-panel-empty">{t("sortableList.noResults")}</div>
                            ) : panelItems.map((item, index) => {
                                const deniedReason = getDeniedReasonText(item);
                                const paidDateText = item?.paidDateTime ? formatDate(item.paidDateTime) : "";
                                const isPaidStatus = item?.status === "Utbetald";
                                const statusMeta = getMobileRowStatusMeta(item?.status);
                                return (
                                    <div key={item?.id ?? index} className="reimbursement-card-item">
                                        <div className="reimbursement-card-content-row">
                                            <div className="reimbursement-card-left">
                                                <div className="reimbursement-card-purpose">{item?.purpose || "-"}</div>
                                                {item?.orgName && <div className="reimbursement-card-org">{item.orgName}</div>}
                                                {isPaidStatus ? (
                                                    <div className="sl-status-paid">
                                                        {paidDateText ? `Utbetald: ${paidDateText}` : "Utbetald"}
                                                    </div>
                                                ) : (
                                                    <div className={`reimbursement-card-status ${statusMeta.statusClass}`}>
                                                        {statusMeta.label}
                                                    </div>
                                                )}
                                                {!!deniedReason && (
                                                    <div className="reimbursement-card-denied-reason">Anledning: "{deniedReason}"</div>
                                                )}
                                            </div>
                                            <div className="reimbursement-card-right">
                                                <div className="reimbursement-card-right-top">
                                                    <div className={`reimbursement-card-amount ${getAmountColorClass(item)}`}>
                                                        {ToCustomDecimal(item?.grossAmount ?? 0)} kr
                                                    </div>
                                                    <div className="reimbursement-card-actions-slot">
                                                        <ActionMenu item={item} />
                                                    </div>
                                                </div>
                                                <div className="reimbursement-card-date">{formatDate(item?.paidDateTime || item?.date || item?.createdAt)}</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </SlidePanel>
        </>
    );
};

export default ReimbursementHeader;